import type {
  AgoraRTCStats,
  ClientRole,
  ConnectionState,
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ILocalTrack,
  NetworkQuality,
  RemoteStreamType,
  RemoteVideoTrackStats,
  UID,
} from 'agora-rtc-sdk-ng'

import { logError, logInfo } from '@core/logging'
import { logMessage, safeLogVariable } from '@core/logging/helpers'

import { agoraAppId } from './utils'

type AgoraNetworkStats = { uid: UID; streamType: RemoteStreamType }

/** A wrapper around AgoraRTCClient. */
export class AgoraRTCClientWrapper {
  #client: IAgoraRTCClient

  /** The token used to join the channel. */
  #token: string | undefined

  /** The user role in a live broadcast channel. */
  #clientRole: ClientRole = 'audience'

  /** The listeners to be called when the client role changes. */
  #clientRoleChangeListeners: Array<(clientRole: ClientRole) => void> = []

  /** The listeners to be called when joining a new channel. */
  #joiningListeners: Array<() => void> = []

  /** The listeners to be called after joined a new channel. */
  #joinedListeners: Array<() => void> = []

  /** The listeners to be called after published a track. */
  #publishedListeners: Array<(track: ILocalTrack) => void> = []

  /** The listeners to be called after unpublished a track. */
  #unpublishedListeners: Array<(track?: ILocalTrack) => void> = []

  /**
   * The listeners to be called when remote user changes, including user join, user left, user publishes and unpublishes any track.
   */
  #remoteUserChangeListeners: Array<() => void> = []

  /**
   * @constructor
   * @param skipDefaultListeners - Whether to skip adding default listeners.
   *     This is needed to simplify the migration for the live stream
   *     AgoraWrapper. We can remove this parameter if we can get rid of the
   *     custom user-published and user-unpublished listener in AgoraWrapper.
   */
  constructor(client: IAgoraRTCClient, skipDefaultListeners = false) {
    this.#client = client

    if (!skipDefaultListeners) {
      this.onUserJoined(async () => {
        this.#remoteUserChangeListeners.forEach((listener) => listener())
      })
      this.onUserLeft(async () => {
        this.#remoteUserChangeListeners.forEach((listener) => listener())
      })
      this.onUserPublished(async (user, mediaType) => {
        await this.#client.subscribe(user, mediaType)
        this.#remoteUserChangeListeners.forEach((listener) => listener())
      })
      this.onUserUnpublished(async (user, mediaType) => {
        await this.#client.unsubscribe(user, mediaType)
        this.#remoteUserChangeListeners.forEach((listener) => listener())
      })
    }

    // Exceptions are not errors, but usually reflect quality issues.
    // Commenting out since there are no immediate action
    /* this.onException((err) => {
      logMessage(`Agora Exception: ${err.msg}`, {
        captureContext: {
          extra: {
            code: err.code,
            uid: err.uid,
            connectionState: this.#client.connectionState,
            error: safeLogVariable(err),
          },
        },
      })
    }) */
  }

  /** Joins an Agora channel. */
  async join(channel: string, token: string, uid?: UID) {
    if (this.#client.connectionState !== 'DISCONNECTED') {
      return
    }
    try {
      if (!token) {
        throw new Error('Token must be provided prior to joining')
      }
      if (!agoraAppId) {
        throw new Error('App id must be provided prior to joining')
      }
      if (!channel) {
        throw new Error('Channel must be provided prior to joining')
      }

      this.#joiningListeners.forEach((listener) => listener())
      await this.#client.join(agoraAppId, channel, token, uid)
      this.#token = token
      this.#joinedListeners.forEach((listener) => listener())
    } catch (err) {
      logError(new Error(`Agora Join Channel Exception: ${err.message}`), {
        captureContext: {
          extra: {
            channel,
            uid,
            connectionState: this.#client.connectionState,
            code: err.code,
            message: err.message,
            name: err.name,
          },
        },
      })
      throw err
    }
  }

  /** Leaves the channel. */
  leave(): Promise<void> {
    return this.#client.leave()
  }

  /** Publishes local track[s]. */
  async publish(track: ILocalTrack | ILocalTrack[]) {
    await this.#client.publish(track)
    this.#publishedListeners.forEach((listener) => {
      if (Array.isArray(track)) {
        track.forEach(listener)
      } else {
        listener(track)
      }
    })
  }

  /**
   * Unpublishes local track[s]. Unpublish all tracks if no track is specified.
   */
  async unpublish(track: ILocalTrack | ILocalTrack[] | undefined) {
    await this.#client.unpublish(track)
    this.#unpublishedListeners.forEach((listener) => {
      if (Array.isArray(track)) {
        track.forEach(listener)
      } else {
        listener(track)
      }
    })
  }

  /** Renews the token. */
  renewToken(token: string): Promise<void> {
    this.#token = token
    return this.#client.renewToken(token)
  }

  /** Sets the client role. */
  async setClientRole(role: ClientRole) {
    try {
      if (this.#clientRole === 'host' && role === 'audience') {
        // CS-1640: Unpublish before changing to audience role or it will throw
        // an error.
        await this.#client.unpublish()
      }
      await this.#client.setClientRole(role)
      this.#clientRole = role
      this.#clientRoleChangeListeners.forEach((callback) => callback(role))
    } catch (err) {
      logError(err)
      throw err
    }
  }

  /** Resets the instance to its initial state. */
  reset() {
    this.#token = undefined
    this.#clientRole = 'audience'
    this.#clientRoleChangeListeners = []
    this.#joiningListeners = []
    this.#joinedListeners = []
    this.#remoteUserChangeListeners = []
    this.#publishedListeners = []
    this.#unpublishedListeners = []
    this.#client.removeAllListeners()
    this.#client.leave()
  }

  /**
   * Registers callback to be triggered when any remote user publishes or
   * unpublishes a track.
   */
  onRemoteUserChange(callback: () => void) {
    this.#remoteUserChangeListeners.push(callback)
    return () => {
      this.#remoteUserChangeListeners = this.#remoteUserChangeListeners.filter(
        (c) => c !== callback,
      )
    }
  }

  /** Registers callback to be triggered when joining a new channel. */
  onJoining(callback: () => void) {
    this.#joiningListeners.push(callback)
    return () => {
      this.#joiningListeners = this.#joiningListeners.filter(
        (c) => c !== callback,
      )
    }
  }

  /** Registers callback to be triggered after joining a new channel. */
  onJoined(callback: () => void) {
    this.#joinedListeners.push(callback)
    return () => {
      this.#joinedListeners = this.#joinedListeners.filter(
        (c) => c !== callback,
      )
    }
  }

  /** Registers callback to be triggered when a local track is published. */
  onPublished(callback: (track: ILocalTrack) => void) {
    this.#publishedListeners.push(callback)
    return () => {
      this.#publishedListeners = this.#publishedListeners.filter(
        (c) => c !== callback,
      )
    }
  }

  /** Registers callback to be triggered when a local track is unpublished. */
  onUnpublished(callback: (track?: ILocalTrack) => void) {
    this.#unpublishedListeners.push(callback)
    return () => {
      this.#unpublishedListeners = this.#unpublishedListeners.filter(
        (c) => c !== callback,
      )
    }
  }

  /** Listens to `volume-indicator` event. */
  onVolumeIndicator(
    callback: (result: { level: number; uid: UID }[]) => void,
  ): () => void {
    this.#client.on('volume-indicator', callback)
    return () => this.#client.off('volume-indicator', callback)
  }

  /** Listens to `connection-state-change` event. */
  onConnectionStateChange(
    callback: (
      curr: ConnectionState,
      rev: ConnectionState,
      reason?: string,
    ) => void,
  ): () => void {
    this.#client.on('connection-state-change', callback)
    return () => this.#client.off('connection-state-change', callback)
  }

  /** Listens to client role changes. */
  onClientRoleChange(callback: (role: ClientRole) => void) {
    this.#clientRoleChangeListeners.push(callback)
    return () => {
      this.#clientRoleChangeListeners = this.#clientRoleChangeListeners.filter(
        (c) => c !== callback,
      )
    }
  }

  /** Listens to `stream-message` event. */
  onStreamMessage(callback: (uid: UID, payload: Uint8Array) => void) {
    this.#client.on('stream-message', callback)
    return () => this.#client.off('stream-message', callback)
  }

  /** Listens to `user-joined` event. */
  onUserJoined(callback: (user: IAgoraRTCRemoteUser) => void) {
    this.#client.on('user-joined', callback)
    return () => this.#client.off('user-joined', callback)
  }

  /** Listens to `user-left` event. */
  onUserLeft(callback: (user: IAgoraRTCRemoteUser) => void) {
    this.#client.on('user-left', callback)
    return () => this.#client.off('user-left', callback)
  }

  /** Listens to `user-published` event. */
  onUserPublished(
    callback: (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => void,
  ) {
    this.#client.on('user-published', callback)
    return () => this.#client.off('user-published', callback)
  }

  /** Listens to `user-unpublished` event. */
  onUserUnpublished(
    callback: (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video') => void,
  ) {
    this.#client.on('user-unpublished', callback)
    return () => this.#client.off('user-unpublished', callback)
  }

  /** Listens to `network-quality` event. */
  onNetworkQuality(
    callback: (data: {
      network_quality: NetworkQuality
      remote_video_stats: Record<string, RemoteVideoTrackStats>
      remote_network_quality: Record<string, NetworkQuality>
    }) => void,
  ) {
    const callbackWithRemoteStats = (networkQuality: NetworkQuality) => {
      callback({
        network_quality: networkQuality,
        remote_video_stats: this.#client.getRemoteVideoStats(),
        remote_network_quality: this.#client.getRemoteNetworkQuality(),
      })
    }
    this.#client.on('network-quality', callbackWithRemoteStats)
    return () => this.#client.off('network-quality', callbackWithRemoteStats)
  }

  /** Listens to `exception` event. */
  onException(
    callback: (err: { code: number; msg: string; uid: UID }) => void,
  ) {
    const exceptionCallback = (event: {
      code: number
      msg: string
      uid: UID
    }) => {
      logMessage('Agora Exception', {
        data: {
          event: safeLogVariable(event),
        },
      })

      callback(event)
    }
    this.#client.on('exception', exceptionCallback)
    return () => this.#client.off('exception', exceptionCallback)
  }

  /** @returns Agora RTC stats */
  getRTCStats(): AgoraRTCStats {
    return this.#client.getRTCStats()
  }

  /**
   * The callback for the `network-quality` event.
   */
  networkQualityLogCallback() {
    // we assert this as IAgoraRTCClient because
    // when this callback is called, its context is
    // the IAgoraRTCClient instance, so "this" refers to this.client
    // when the event fires
    const remoteVideoStats = (
      this as unknown as IAgoraRTCClient
    ).getRemoteVideoStats()
    const remoteNetworkQuality = (
      this as unknown as IAgoraRTCClient
    ).getRemoteNetworkQuality()

    logInfo('Agora Quality', {
      uid: (this as unknown as IAgoraRTCClient).uid,
      remote_video_stats: remoteVideoStats,
      remote_network_quality: remoteNetworkQuality,
    })
  }

  /**
   * A callback that fires when the stream type changes
   */
  streamTypeChangedCallback(stats: AgoraNetworkStats) {
    logInfo('Agora Quality', 'Agora Quality - Stream Type Change', stats)
  }

  /**
   * Enables the logging out of quality statistics to the browser console. on 'network-quality' event.
   */
  enableQualityLogging() {
    this.#client.on('network-quality', this.networkQualityLogCallback)
    this.#client.on('stream-type-changed', this.streamTypeChangedCallback)
  }

  /** @returns The current remote users. */
  get remoteUsers() {
    return this.#client.remoteUsers
  }

  /** @returns The tracks that the local user is publishing. */
  get localTracks() {
    return this.#client.localTracks
  }

  /** @returns The current connection state. */
  get connectionState() {
    return this.#client.connectionState
  }

  /** @returns The current client role. */
  get clientRole() {
    return this.#clientRole
  }

  /** @returns The current user id. */
  get uid() {
    return this.#client.uid
  }

  /** @returns The current token used to join the channel. */
  get token() {
    return this.#token
  }

  /** @returns AgoraRTCClient. */
  get client() {
    return this.#client
  }
}
